import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { IApiResponseBody } from '../../../common/models/common';
import { RootState } from '../../app.model';
import { booleanLookup } from '../../../common/constants/common';
import { ISpsApiTypeState } from './apiType.model';
import {
  deleteSpsApiType,
  getSpsApiTypeById,
  saveMappingSpsApiType,
  saveSpsApiType,
  searchSpsApiType,
} from './apiType.action';
import { ISpsApiType } from '../../../services/sps/apiType/apiType.model';

export const initialState: ISpsApiTypeState = {
  search: {
    loading: false,
    hasErrors: false,
    data: [],
    count: 0,
    lookups: {},
    tableName: '',
  },
  tableColumnSelection: {
    id: null,
    table_name: null,
    columns: {},
  },
  getById: {
    loading: false,
    hasErrors: false,
    data: null,
  },
  save: {
    loading: false,
    hasErrors: false,
    messages: [],
    data: null,
  },
  saveMapping: {
    loading: false,
    hasErrors: false,
    messages: [],
  },
  delete: {
    loading: false,
    hasErrors: false,
    messages: [],
  },
};

export const spsApiTypeSlice = createSlice({
  name: 'spsApiType',
  initialState,
  reducers: {
    clearSpsApiType: () => {
      return initialState;
    },
    clearSpsApiTypeMessages: (state) => {
      state.save.messages = [];
      state.delete.messages = [];
    },
    clearSpsApiTypeGetById: (state) => {
      state.getById.data = null;
    },
    setTableColumnSelection: (state, action: PayloadAction<{ [key: string]: boolean }>) => {
      state.tableColumnSelection.columns = action.payload;
    },
  },
  extraReducers: {
    // Search
    [searchSpsApiType.pending.type]: (state) => {
      state.search.loading = true;
    },
    [searchSpsApiType.fulfilled.type]: (state, action: PayloadAction<any>) => {
      const { search_result, ...rest } = action.payload;
      state.search.data = search_result.records;
      state.search.count = search_result.total_count;
      if (JSON.stringify(rest) !== '{}') {
        state.search.lookups = { ...rest, booleanLookup };
      }
      state.search.loading = false;
      state.search.hasErrors = false;
      state.search.tableName = search_result.table_name;
      if (search_result.column_selection) {
        state.tableColumnSelection.id = search_result.column_selection.id;
        const tableSelectionObj = JSON.parse(search_result.column_selection.columns as any);
        if (tableSelectionObj.columns) {
          state.tableColumnSelection.column_orders = tableSelectionObj.column_orders;
          state.tableColumnSelection.columns = tableSelectionObj.columns;
        } else {
          state.tableColumnSelection.columns = tableSelectionObj;
        }
      }
      state.tableColumnSelection.table_name = search_result.table_name;
    },
    [searchSpsApiType.rejected.type]: (state) => {
      state.search.loading = false;
      state.search.hasErrors = true;
    },

    // Get by id
    [getSpsApiTypeById.pending.type]: (state) => {
      state.getById.loading = true;
    },
    [getSpsApiTypeById.fulfilled.type]: (state, action: PayloadAction<ISpsApiType>) => {
      state.getById.data = action.payload;
      state.getById.loading = false;
      state.getById.hasErrors = false;
    },
    [getSpsApiTypeById.rejected.type]: (state) => {
      state.getById.loading = false;
      state.getById.hasErrors = true;
    },

    // Save
    [saveSpsApiType.pending.type]: (state) => {
      state.save.loading = true;
      state.save.messages = [];
    },
    [saveSpsApiType.fulfilled.type]: (state, action: PayloadAction<IApiResponseBody<unknown>>) => {
      state.save.loading = false;
      state.save.hasErrors = false;
      state.save.messages = action.payload.messages;
      state.save.data = action.payload?.data;
    },
    [saveSpsApiType.rejected.type]: (state) => {
      state.save.loading = false;
      state.save.hasErrors = true;
    },

    // Save Mapping
    [saveMappingSpsApiType.pending.type]: (state) => {
      state.saveMapping.loading = true;
      state.saveMapping.messages = [];
    },
    [saveMappingSpsApiType.fulfilled.type]: (state, action: PayloadAction<IApiResponseBody<unknown>>) => {
      state.saveMapping.loading = false;
      state.saveMapping.hasErrors = false;
      state.saveMapping.messages = action.payload.messages;
    },
    [saveMappingSpsApiType.rejected.type]: (state) => {
      state.saveMapping.loading = false;
      state.saveMapping.hasErrors = true;
    },

    // Delete
    [deleteSpsApiType.pending.type]: (state) => {
      state.delete.loading = true;
      state.delete.messages = [];
    },
    [deleteSpsApiType.fulfilled.type]: (
      state,
      action: PayloadAction<IApiResponseBody<unknown>>
    ) => {
      state.delete.loading = false;
      state.delete.hasErrors = false;
      state.delete.messages = action.payload.messages;
    },
    [deleteSpsApiType.rejected.type]: (state) => {
      state.delete.loading = false;
      state.delete.hasErrors = true;
    },
  },
});

// A selector
export const spsApiTypeSelector = (state: RootState) => state.spsApiType;

// Actions
export const {
  clearSpsApiType,
  clearSpsApiTypeMessages,
  clearSpsApiTypeGetById,
  setTableColumnSelection,
} = spsApiTypeSlice.actions;

// The reducer
export default spsApiTypeSlice.reducer;
